import {ReactElement, ReactNode, useMemo} from 'react'
import Button from '@material-ui/core/Button'
import {Trans} from 'react-i18next'

import messages from '../assets/messages'
import {useVideoVerificationContext} from './VideoVerificationProvider'
import {withdrawalStatuses} from '@bdswiss/common-enums'

import LoadingButton from '../components/Common/LoadingButton'
import {canCancelWithdrawal} from '../components/Payments/History/PaymentsHistoryHelper'
import {canContinueVerification, HistoryVerificationState, historyStateOf} from './utils'
import './video-verification.css'

type HistoryRow = {id: number; transactionType?: string}

const CHIPS: Record<HistoryVerificationState, {className: string; message: {i18nKey: string; defaults: string}}> = {
  waiting: {className: 'vv-chip--waiting', message: messages.videoVerificationStatusWaiting},
  review: {className: 'vv-chip--review', message: messages.videoVerificationStatusInReview},
  hold: {className: 'vv-chip--hold', message: messages.videoVerificationStatusOnHold},
}

/** The state of a History row that is held for the video verification, or null for any other row. */
function useRowState(row: HistoryRow): HistoryVerificationState | null {
  const {heldWithdrawals, status} = useVideoVerificationContext()
  const heldIds = useMemo(() => new Set(heldWithdrawals.map((withdrawal) => withdrawal.id)), [heldWithdrawals])
  return historyStateOf(row, heldIds, status)
}

/** The status chip of a held withdrawal. Any other row keeps the status that History shows today. */
function VideoVerificationStatus({row, fallback}: {row: HistoryRow; fallback: ReactNode}): ReactElement {
  const state = useRowState(row)
  if (!state) return <>{fallback}</>

  const {className, message} = CHIPS[state]
  return (
    <span className={`vv-chip ${className}`} data-testid={`vv-chip-${state}`}>
      <Trans {...message} />
    </span>
  )
}

/** The row link "Continue video verification". It shows while the client can still do the check. */
function ContinueVideoVerificationLink({row, className}: {row: HistoryRow; className?: string}): ReactElement | null {
  const state = useRowState(row)
  const {openVideoVerification} = useVideoVerificationContext()
  if (!canContinueVerification(state)) return null

  return (
    <Button
      disableFocusRipple
      disableRipple
      variant="text"
      color="primary"
      classes={{root: className}}
      onClick={() => openVideoVerification()}
    >
      <Trans {...messages.videoVerificationRowContinue} />
    </Button>
  )
}

type CancelHeldWithdrawalButtonProps = {
  row: HistoryRow & Record<string, any>
  disabled?: boolean
  className?: string
  onCancel: (row: any) => Promise<unknown> | void
}

/**
 * The 24-hour cancel of a held withdrawal. It unlocks the amount; the flag stays (spec, "Client cancel").
 * History reads the held state again afterwards, or banner H would stay.
 */
function CancelHeldWithdrawalButton({row, disabled, className, onCancel}: CancelHeldWithdrawalButtonProps): ReactElement | null {
  const state = useRowState(row)
  const {refetch} = useVideoVerificationContext()
  if (!state || !canCancelWithdrawal(row, [withdrawalStatuses.onHold.key])) return null

  return (
    <LoadingButton
      disableFocusRipple
      disableRipple
      variant="text"
      color="primary"
      disabled={Boolean(disabled)}
      status=""
      size="medium"
      classes={{root: className}}
      onClick={() => Promise.resolve(onCancel(row)).then(() => refetch())}
    >
      <Trans {...messages.cancel} />
    </LoadingButton>
  )
}

export {VideoVerificationStatus, ContinueVideoVerificationLink, CancelHeldWithdrawalButton}
